import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';
import { UseMutationResult } from 'react-query';
import { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';
import { components } from "react-select";
import { MultiValue, SingleValue, StylesConfig, ValueContainerProps, } from 'react-select/dist/declarations/src';
import styles from "./MultipleSelectBoxApiCall.module.css";
import { Badge } from 'reactstrap';
import { MisterIcon } from '@rtel/mister-icons';

const MultipleSelectBoxApiCall = (props: MultipleSelectBoxApiCallProps) => {
  const { setFieldValue, values } = useFormikContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [moreValuesExistInDB, setMoreValuesExistInDB] = useState(false);
  const [allOptionValues,  setAllOptionValues] = useState<ISearchDropdown []>([]);

  const maxResults = 100;

  const customStyle = {
    bgSelectedColor: "#f5f7fe",
    bgHoverColor: "#dbe0f3",
    textColor: "#495057"
  }


  const debouncedPromiseOptions = debounce (
    async (inputValue: string, callback: (options: any) => void) => {
      const searchResult = await props.searchMutation.mutateAsync({
        data: inputValue
      });
      searchResult.length >= maxResults ? setMoreValuesExistInDB(true) : setMoreValuesExistInDB(false);
      setAllOptionValues(searchResult);
      callback(searchResult);
    },
    props.debounceTime
  );

  const promiseOptions = (inputValue: string, callback: (options: any) => void) => {
      debouncedPromiseOptions(inputValue, callback);
  };

  const onMenuOpen = () => setIsMenuOpen(true);

  const onMenuClose = () => {
    setIsMenuOpen(false);
    setMoreValuesExistInDB(false);   
  }

  const handleInputChange = (input: string) => {
    if (input == ""){
      setMoreValuesExistInDB(false);
    }
  }

  const triggerProps = (formikElementProps: any, currentSelectedOption: any) => {
    let newValues: any;
    formikElementProps.selectOption(currentSelectedOption);

    if(!props.value) { return };

    if (props.value.includes(currentSelectedOption)) {
      // If the item is already in the array, remove it
      newValues = props.value.filter((item) => item !== currentSelectedOption);
    } else {
      // If the item is not in the array, add it
      newValues = [...props.value, currentSelectedOption];
    }
    
    if (props.onChange) { 
      props.onChange(newValues, currentSelectedOption)
    };
    
  }

  const MenuList = (props: any) => {
    if (props.isLoading) {
      return <components.MenuList {...props} />;
    }

    const selectedOptions = props.children?.filter?.((childNode: any) => childNode.props.isSelected) ?? [];
    const nonSelectedOptions = props.children?.filter?.((childNode: any) => !childNode.props.isSelected) ?? [];
    const childrenToRender = [...selectedOptions, ...nonSelectedOptions];
    return (
      <>
        <components.ValueContainer {...props}>
          {childrenToRender.length > 0 && `${childrenToRender.length} items`}
        </components.ValueContainer >
        <components.MenuList {...props} className={styles.menuContainer}>
          {childrenToRender.length > 0 ?
            <div >
              <div className={styles.optionsContainer}>
                {childrenToRender.map((option, index) => (
                  <label
                    key={index}
                    className='d-flex mb-0'
                    style={{background: option.props.isSelected ? customStyle.bgSelectedColor : "#ffffff" }}
                    onClick={() => triggerProps(props, option.props.data)}
                  >
                    <input
                      type="checkbox"
                      checked={option.props.isSelected}
                      onChange={() => props.selectOption(option.props.data)}
                      className='d-flex'
                      style={{margin: "10px", background: option.props.isSelected ? customStyle.bgSelectedColor : "#ffffff" }}
                    />
                    <div className='d-flex flex-fill me-2'>{option}</div>
                  </label>
                ))}
              </div>
              {allOptionValues.length > 1 && 
                 <span
                 className={styles.selectAll}
                 onClick={() => selectAllAction(props)}
               >
                 Select All
               </span>
              }
           
            </div>
          :
            <components.NoOptionsMessage {...props}>
              <span className="custom-css-class">No Options</span>
            </components.NoOptionsMessage>
          }

        </components.MenuList>
      </>
    );
  };

  const ValueContainer = useCallback(({
    children,
    ...props
  }: ValueContainerProps<ISearchDropdown>) => {
    let [values, input] = children as any; //dont delete this unused "values"
    let counterValuesMessage: string = "";

    const valuesArray: ISearchDropdown[] = Array.isArray(props.selectProps.value)
    ? props.selectProps.value
    : props.selectProps.value ? [props.selectProps.value] : [];

    if (valuesArray) {
      const plural = valuesArray.length === 1 ? "" : "s";
      counterValuesMessage = `${valuesArray.length} item${plural} selected`;
    }

    return (
      <components.ValueContainer {...props}>
        {input}
        {valuesArray.length > 0 && (
          <Badge color="info">{counterValuesMessage}</Badge>
        )}
      </components.ValueContainer>
    );
  },[]);

  const selectAllAction = (reactSelectProps: any) => {
    if (!props.value) { return ;}
    let ids = new Set(props.value.map(d => d.value));
    let merged = [...props.value, ...allOptionValues.filter(d => !ids.has(d.value))];

    triggerHandleChange(merged);
    reactSelectProps.setValue(merged);
    if(props.onChange){
      props.onChange(merged, []);
    }
  }

  const triggerHandleChange = (val: MultiValue<ISearchDropdown> | SingleValue<ISearchDropdown>) => {
    const _val = val as ISearchDropdown[] | ISearchDropdown;
    let sortedValues: ISearchDropdown[] | ISearchDropdown = _val;

    const isArray = Array.isArray(_val);
    if (isArray) {
      sortedValues = [..._val].sort((a, b) =>
        a.label > b.label ? 1 : -1,
      );
    }
    setFieldValue(props.name, sortedValues);
  }

  const selectStyle: StylesConfig<ISearchDropdown, boolean> = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? customStyle.bgSelectedColor : "#fffff",
      color: customStyle.textColor,
      '&:hover': {
        color: customStyle.textColor,
        background: customStyle.bgHoverColor,
      },
    })
  }

  const ClearIndicator = (clearProps: any) => {
    return (
      <components.DropdownIndicator
        {...clearProps}
        innerProps={{
          ...clearProps.innerProps,
        }}
      >
        <div
          onMouseDown={() => {
            if(props.onChange) {
              props?.onChange([], [clearProps.options])}
            }
          }
          onTouchEnd={() => {
            if(props.onChange) {
              props?.onChange([], [clearProps.options])}
            }
          }
        >
          <MisterIcon name='XClose' size={'sm'} />
        </div>
      </components.DropdownIndicator>
    )
  }

  return (
    <>
      {!!moreValuesExistInDB && !!isMenuOpen && (
        <blockquote className={`bg-warning p-1`}>Over {maxResults} results found. Please refine your search.</blockquote>
      )}

      <AsyncSelect
        //cacheOptions
        isMulti
        defaultOptions={props.value}
        isSearchable
        value={props.value}
        onChange={triggerHandleChange}
        loadOptions={promiseOptions}
        className={props.className}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onInputChange={handleInputChange}
        components={{ MenuList, ValueContainer, ClearIndicator }}
        styles={selectStyle}
        menuIsOpen={isMenuOpen}
      />
    </>
  );
}

export default MultipleSelectBoxApiCall;

export interface ISearchDropdown {
  value: string;
  label: string;
}

interface MultipleSelectBoxApiCallProps {
  searchMutation: UseMutationResult<ISearchDropdown[], AxiosError<unknown, any>, { data: string; }, unknown>;
  options?: ISearchDropdown [];
  value: ISearchDropdown [] | undefined;
  debounceTime?: number;
  name: string;
  className?: string;
  onChange?: (allSelectedData: any, changedData: any) => void;

}

MultipleSelectBoxApiCall.defaultProps = {
  debounceTime: 300,
  className: "",
  value: [],
}